import { PortableText } from "@portabletext/react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Animate } from "../../components/animate";
import { Layout } from "../../components/layout";
import { jobPostByIdQuery } from "../../lib/queries";
import { client } from "../../lib/sanityClient";

const formatDate = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-NG", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const descriptionComponents = {
  block: {
    normal: ({ children }) => (
      <p className="mb-5 text-base leading-7 text-grey-700 lg:text-lg">
        {children}
      </p>
    ),
    h2: ({ children }) => (
      <h2 className="mb-4 mt-10 text-2xl font-medium text-deep-primary lg:text-3xl">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="mb-3 mt-8 text-xl font-medium text-deep-primary">
        {children}
      </h3>
    ),
    blockquote: ({ children }) => (
      <blockquote className="my-6 border-l-4 border-primary pl-5 text-lg italic text-grey-600">
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="mb-5 list-disc space-y-2 pl-5 text-base leading-7 text-grey-700 lg:text-lg">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="mb-5 list-decimal space-y-2 pl-5 text-base leading-7 text-grey-700 lg:text-lg">
        {children}
      </ol>
    ),
  },
  marks: {
    strong: ({ children }) => (
      <strong className="font-semibold text-deep-primary">{children}</strong>
    ),
    link: ({ value, children }) => (
      <a
        href={value?.href}
        target="_blank"
        rel="noreferrer"
        className="text-primary underline"
      >
        {children}
      </a>
    ),
  },
};

const JobDetailsSkeleton = () => (
  <div className="max-width animate-pulse py-12 lg:py-20">
    <div className="h-4 w-28 rounded bg-grey-100" />
    <div className="mt-6 h-12 w-3/4 rounded bg-grey-100" />
    <div className="mt-4 h-5 w-1/2 rounded bg-grey-100" />
    <div className="mt-12 max-w-[760px] space-y-4">
      {[1, 2, 3, 4, 5].map((item) => (
        <div key={item} className="h-5 rounded bg-grey-100" />
      ))}
    </div>
  </div>
);

export const JobDetailsPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client
      .fetch(jobPostByIdQuery, {
        jobId,
        test: process.env.NODE_ENV === "development",
      })
      .then((data) => {
        if (!data) navigate("/careers", { replace: true });
        else setJob(data);
      })
      .catch(() => navigate("/careers", { replace: true }))
      .finally(() => setLoading(false));
  }, [jobId, navigate]);

  if (loading) {
    return (
      <Layout>
        <JobDetailsSkeleton />
      </Layout>
    );
  }

  if (!job) return null;

  return (
    <Layout>
      <div className="w-full">
        <div className="bg-light-yellow">
          <div className="max-width py-8 lg:py-12">
            <Link
              to="/careers"
              className="text-sm font-medium text-grey-500 transition-colors hover:text-primary"
            >
              ← Back to careers
            </Link>

            <Animate animation="fade-down">
              <div className="mt-8 max-w-[820px]">
                <div className="mb-4 flex flex-wrap gap-2 text-xs font-semibold uppercase tracking-wider">
                  {job.employmentType && (
                    <span className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                      {job.employmentType}
                    </span>
                  )}
                  {job.remote && (
                    <span className="rounded-full bg-green/10 px-3 py-1 text-green">
                      Remote
                    </span>
                  )}
                </div>
                <h1 className="text-[40px] font-medium leading-tight tracking-[-0.03em] text-deep-primary lg:text-[64px]">
                  {job.title}
                </h1>
                <div className="mt-4 flex flex-wrap gap-x-2 gap-y-1 text-base text-grey-600">
                  {job.company && <span>{job.company}</span>}
                  {job.location && <span>· {job.location}</span>}
                  {job.publishedAt && (
                    <span>· Posted {formatDate(job.publishedAt)}</span>
                  )}
                </div>
              </div>
            </Animate>
          </div>
        </div>

        <div className="max-width py-12 lg:py-20">
          <div className="max-w-[760px]">
            {job.description && (
              <PortableText
                value={job.description}
                components={descriptionComponents}
              />
            )}

            {job.requirements?.length > 0 && (
              <section className="mt-10 border-t border-grey-200 pt-8">
                <h2 className="mb-5 text-2xl font-medium text-deep-primary lg:text-3xl">
                  What you&apos;ll bring
                </h2>
                <ul className="list-disc space-y-3 pl-5 text-base leading-7 text-grey-700 lg:text-lg">
                  {job.requirements.map((requirement, index) => (
                    <li key={`${job._id}-requirement-${index}`}>
                      {requirement}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <div className="mt-12 border-t border-grey-200 pt-8">
              <a
                href={job.applyUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-deep-primary"
              >
                Apply for this role{" "}
                <span className="ml-2" aria-hidden="true">
                  ↗
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
